import axios from "axios";
import { clearSessionAndNotify } from "./session";

// CRA inlines REACT_APP_* at build time, so the deployed bundle carries whatever
// was set when it was built. Deployments must supply REACT_APP_API_URL (build
// environment variable on Cloudflare Pages); silently falling back to localhost
// would ship a site that calls the visitor's own machine and fails obscurely.
if (process.env.NODE_ENV === "production" && !process.env.REACT_APP_API_URL) {
    throw new Error(
        "REACT_APP_API_URL is not set. Set it as a build environment variable " +
        "for this deployment (see frontend/.env.example)."
    );
}

export const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:8000";

export const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request Interceptor: Inject Token
client.interceptors.request.use((config) => {
    if (!config._skipAuth) {
        const token = localStorage.getItem("access_token");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Token refresh state — prevents race conditions when multiple
// requests receive 401 simultaneously.
let isRefreshing = false;
let refreshQueue = [];

function processQueue(token) {
    refreshQueue.forEach(({ resolve, reject }) => {
        if (token) {
            resolve(token);
        } else {
            reject(new Error("Token refresh failed"));
        }
    });
    refreshQueue = [];
}

// Response Interceptor: Handle 401 with automatic token refresh
client.interceptors.response.use((response) => {
    return response;
}, async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest._skipAuth) {
        originalRequest._retry = true;

        // If already refreshing, queue this request until new token arrives
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                refreshQueue.push({ resolve, reject });
            }).then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return client(originalRequest);
            });
        }

        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
            isRefreshing = true;
            try {
                const { data } = await axios.post(
                    `${client.defaults.baseURL}/api/users/token/refresh/`,
                    { refresh: refreshToken }
                );
                localStorage.setItem("access_token", data.access);
                if (data.refresh) {
                    localStorage.setItem("refresh_token", data.refresh);
                }
                processQueue(data.access);
                originalRequest.headers.Authorization = `Bearer ${data.access}`;
                return client(originalRequest);
            } catch {
                processQueue(null);
                clearSessionAndNotify();
            } finally {
                isRefreshing = false;
            }
        } else {
            clearSessionAndNotify();
        }
    }
    return Promise.reject(error);
});
